import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import GoldDivider from '../components/GoldDivider';
import API_URL from '../config.js';

export default function RSVP() {
  const { t } = useLanguage();

  // Step 1: code validation
  const [codeInput, setCodeInput] = useState('');
  const [validatedCode, setValidatedCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState('');

  // Step 2: RSVP form
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    attendance: 'solo',
    dietary_needs: '',
    message: '',
    gdpr_consent: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');

  // ── Step 1 handlers ──────────────────────────────────

  async function handleCodeSubmit(e) {
    e.preventDefault();
    setCodeError('');
    const code = codeInput.trim().toUpperCase();
    if (!code) return;

    setCodeLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('rsvp.code_invalid'));
      }

      setValidatedCode(code);
    } catch (err) {
      setCodeError(err.message || t('rsvp.code_error'));
    } finally {
      setCodeLoading(false);
    }
  }

  // ── Step 2 handlers ──────────────────────────────────

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setFieldError('');
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setFieldError('');

    if (!form.first_name.trim() || !form.last_name.trim()) {
      setFieldError(t('rsvp.required_fields'));
      return;
    }
    if (!form.gdpr_consent) {
      setFieldError(t('rsvp.required_fields'));
      return;
    }

    setLoading(true);
    try {
      const attendanceMap = {
        solo:       { attending: true,  plus_ones: 0 },
        with_guest: { attending: true,  plus_ones: 1 },
        no:         { attending: false, plus_ones: 0 },
      };
      const { attendance, ...formData } = form;
      const { attending, plus_ones } = attendanceMap[attendance] || attendanceMap.solo;

      const res = await fetch(`${API_URL}/api/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          attending,
          plus_ones,
          invite_code: validatedCode,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('rsvp.error'));
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || t('rsvp.error'));
    } finally {
      setLoading(false);
    }
  }

  // ── Success screen ───────────────────────────────────

  if (submitted) {
    return (
      <div className="page-container" style={{ paddingTop: '2rem' }}>
        <div className="success-box" style={{ maxWidth: '560px', margin: '2rem auto' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <h3
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: 'var(--gold-light)',
              marginBottom: '0.5rem',
            }}
          >
            {t('rsvp.success_title')}
          </h3>
          <p style={{ color: 'var(--cream)' }}>{t('rsvp.success_text')}</p>
        </div>
      </div>
    );
  }

  // ── Step 1: Code validation ──────────────────────────

  if (!validatedCode) {
    return (
      <div className="page-container" style={{ paddingTop: '2rem' }}>
        <h1 className="page-title">{t('rsvp.title')}</h1>
        <p className="page-subtitle">{t('rsvp.subtitle')}</p>
        <GoldDivider />

        <div className="card" style={{ maxWidth: '460px', margin: '2rem auto', textAlign: 'center' }}>
          {/* Eyebrow tag */}
          <p
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: '0.65rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--gold)',
              marginBottom: '1rem',
            }}
          >
            CODE D'INVITATION / EINLADUNGSCODE
          </p>

          <h2
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '1.8rem',
              color: 'var(--cream)',
              marginBottom: '0.5rem',
            }}
          >
            {t('rsvp.code_title')}
          </h2>
          <p
            style={{
              color: 'rgba(245,233,198,0.65)',
              fontSize: '0.92rem',
              marginBottom: '2rem',
            }}
          >
            {t('rsvp.code_subtitle')}
          </p>

          <form onSubmit={handleCodeSubmit} noValidate>
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="invite_code">{t('rsvp.code_label')}</label>
              <input
                id="invite_code"
                type="text"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value.toUpperCase().slice(0, 6));
                  setCodeError('');
                }}
                placeholder={t('rsvp.code_placeholder')}
                maxLength={6}
                autoComplete="off"
                spellCheck={false}
                style={{
                  width: '100%',
                  textAlign: 'center',
                  fontSize: '1.8rem',
                  letterSpacing: '0.5em',
                  fontFamily: "'Cinzel', serif",
                  textTransform: 'uppercase',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(201,168,76,0.3)',
                  borderRadius: '3px',
                  padding: '0.75rem 1rem',
                  color: 'var(--cream)',
                  outline: 'none',
                }}
              />
            </div>

            {codeError && (
              <div className="error-box" style={{ marginBottom: '1rem' }}>
                {codeError}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={codeLoading || codeInput.trim().length === 0}
              style={{
                minWidth: '220px',
                opacity: codeLoading || codeInput.trim().length === 0 ? 0.7 : 1,
              }}
            >
              {codeLoading ? t('common.loading') : t('rsvp.code_validate')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Step 2: RSVP form ────────────────────────────────

  return (
    <div className="page-container" style={{ paddingTop: '2rem' }}>
      <h1 className="page-title">{t('rsvp.title')}</h1>
      <p className="page-subtitle">{t('rsvp.subtitle')}</p>
      <GoldDivider />

      <div className="card" style={{ maxWidth: '680px', margin: '0 auto' }}>
        {/* Validated code badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(201,168,76,0.12)',
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: '3px',
            padding: '0.35rem 0.85rem',
            marginBottom: '1.5rem',
            fontFamily: "'Cinzel', serif",
            fontSize: '0.7rem',
            letterSpacing: '0.15em',
            color: 'var(--gold)',
          }}
        >
          ✦ {t('rsvp.code_validated')} : {validatedCode}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Name row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label htmlFor="first_name">{t('rsvp.first_name')}</label>
              <input
                id="first_name"
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                required
                autoComplete="given-name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="last_name">{t('rsvp.last_name')}</label>
              <input
                id="last_name"
                type="text"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          {/* Contact row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label htmlFor="email">{t('rsvp.email')}</label>
              <input
                id="email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">{t('rsvp.phone')}</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                autoComplete="tel"
              />
            </div>
          </div>

          {/* Attendance — 3 options */}
          <div className="form-group">
            <label>{t('rsvp.attendance')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
              {[
                { value: 'solo',       label: t('rsvp.attendance_solo') },
                { value: 'with_guest', label: t('rsvp.attendance_with_guest') },
                { value: 'no',         label: t('rsvp.attendance_no') },
              ].map(({ value, label }) => (
                <div className="form-check" key={value}>
                  <input
                    id={`attendance_${value}`}
                    type="radio"
                    name="attendance"
                    value={value}
                    checked={form.attendance === value}
                    onChange={handleChange}
                  />
                  <label htmlFor={`attendance_${value}`}>{label}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Dietary needs */}
          <div className="form-group">
            <label htmlFor="dietary_needs">{t('rsvp.dietary_needs')}</label>
            <textarea
              id="dietary_needs"
              name="dietary_needs"
              value={form.dietary_needs}
              onChange={handleChange}
              rows={3}
            />
          </div>

          {/* Personal message */}
          <div className="form-group">
            <label htmlFor="message">{t('rsvp.message')}</label>
            <textarea
              id="message"
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={3}
            />
          </div>

          {/* GDPR consent */}
          <div className="form-check" style={{ marginBottom: '1.5rem' }}>
            <input
              id="gdpr_consent"
              type="checkbox"
              name="gdpr_consent"
              checked={form.gdpr_consent}
              onChange={handleChange}
              required
            />
            <label htmlFor="gdpr_consent">
              {t('rsvp.gdpr_consent')}{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gold)', textDecoration: 'underline' }}
              >
                (RGPD)
              </a>
            </label>
          </div>

          {/* Errors */}
          {fieldError && (
            <div className="error-box" style={{ marginBottom: '1rem' }}>
              {fieldError}
            </div>
          )}
          {error && (
            <div className="error-box" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{ minWidth: '220px', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? t('common.loading') : t('rsvp.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
